import React, { useEffect } from 'react'
import { Box, FormHelperText } from '@mui/material'
import { getIn, useField, useFormikContext } from 'formik'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import { AutoCompleteGoogleMapFieldProps } from '../autoCompleteGoogleMapField/intefaces'
import LocationsPanel from './LocationsPanel'
import { LocationTypeOption } from './LocationsRuleBar'

type LocationsFieldExtraProps = {
  /** Id of the search input, also used to anchor the Google Places service */
  autocompleteId?: string
  /** Opens the CSV import, shown in the panel header */
  onImportCsv?: () => void
  /** The locations are excluded rather than targeted; read from `ruleFieldName` when it is set */
  exclusive?: boolean
  /** The form field of the rule (`true` excludes): the panel's top bar edits it */
  ruleFieldName?: string
  /** The form field of the location type: the panel's top bar edits it */
  typeFieldName?: string
  locationTypeOptions?: LocationTypeOption[]
}

/** Edit mode: the panel's own input searches the targeted locations and adds new ones */
const LocationsField: React.FC<AutoCompleteGoogleMapFieldProps & LocationsFieldExtraProps> = ({
  name,
  locationType = LocationTypesEnum.Cities,
  countryCode = 'fr',
  compact = false,
  autocompleteId = 'geoLocationsPanelSearch',
  onImportCsv,
  exclusive = false,
  ruleFieldName,
  typeFieldName,
  locationTypeOptions,
}) => {
  const t = useAppTranslations()
  const [, meta] = useField({ name })
  const hasError = Boolean(meta.touched && meta.error)
  const { values, setFieldValue } = useFormikContext<unknown>()

  // The options depend on the rule (Countries can be dropped when going back to Include): never keep a type left out
  useEffect(() => {
    if (!typeFieldName || !locationTypeOptions?.length) return
    if (!locationTypeOptions.some((option) => option.value === locationType))
      setFieldValue(typeFieldName, locationTypeOptions[0].value)
  }, [typeFieldName, locationTypeOptions, locationType, setFieldValue])

  return (
    <Box sx={{ width: '100%', mt: compact ? 0 : 2 }}>
      {/* A new list (another location type) starts with a fresh selection, search, page and sort */}
      <LocationsPanel
        key={name}
        name={name}
        locationType={locationType}
        mode="edit"
        compact={compact}
        countryCode={countryCode}
        autocompleteId={autocompleteId}
        error={hasError}
        onImportCsv={onImportCsv}
        exclusive={ruleFieldName ? Boolean(getIn(values, ruleFieldName)) : exclusive}
        onExclusiveChange={ruleFieldName ? (value) => setFieldValue(ruleFieldName, value) : undefined}
        locationTypeOptions={locationTypeOptions}
        onLocationTypeChange={typeFieldName ? (value) => setFieldValue(typeFieldName, value) : undefined}
      />
      <FormHelperText error={hasError}>{(hasError && meta.error && t(meta.error)) || ' '}</FormHelperText>
    </Box>
  )
}

export default LocationsField
